import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { RootState } from 'store/configureStore';
import ConfigService from '../../../services/config/index';
import { Popups } from 'commons/components/popups/popups';
import { parseApiError } from 'commons/error';
import {
  SystemConfig,
  SystemConfigInvoice,
  Settings,
  SmtpSettings,
  SmtpTestResult,
} from 'types/type';

const configService = new ConfigService();

type ITA = ThunkAction<Promise<void>, RootState, unknown, AnyAction>;
type IDA = ThunkDispatch<unknown, unknown, AnyAction>;

const startFetch = (): { type: string } => {
  return { type: 'BEGIN_FETCH' };
};

const endFetch = (): { type: string } => {
  return { type: 'FETCH_COMPLETED' };
};

export const getDayToDelete =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const days: number = await configService.getDaysToDelete();
      dispatch({ type: 'DAYS_TO_DELETE', days });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getMonthToDelete =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const month: number = await configService.getMonthsToDelete();
      dispatch({ type: 'MONTHS_TO_DELETE', month });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getYearToDelete =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const years: number = await configService.getYearsToDelete();
      dispatch({ type: 'YEARS_TO_DELETE', years });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getDayToDeleteInvoice =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const daysInvoice: number = await configService.getDaysToDeleteInvoice();
      dispatch({ type: 'DAYS_TO_DELETE_INVOICE', daysInvoice });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getMonthToDeleteInvoice =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const monthsInvoice: number =
        await configService.getMonthsToDeleteInvoice();
      dispatch({ type: 'MONTHS_TO_DELETE_INVOICE', monthsInvoice });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getYearToDeleteInvoice =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const yearsInvoice: number =
        await configService.getYearsToDeleteInvoice();
      dispatch({ type: 'YEARS_TO_DELETE_INVOICE', yearsInvoice });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getSystemConfig =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const systemConfig: SystemConfig = await configService.getSystemConfig();
      dispatch({ type: 'SYS_CONF', systemConfig });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getSystemConfigInvoice =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const systemConfigInvoice: SystemConfigInvoice =
        await configService.getSystemConfigInvoice();
      dispatch({ type: 'SYS_CONF_INV', systemConfigInvoice });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };
export const getSystemSettings =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const settings: Settings = await configService.getSystemSettings();
      dispatch({ type: 'SYS_SETTINGS', settings });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

export const getSmtpSettings =
  (): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      const smtpSettings: SmtpSettings = await configService.getSmtpConfig();
      dispatch({ type: 'SMTP_SETTINGS', smtpSettings });
      dispatch(endFetch());
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };

/**
 * Boton "Test": envia un correo de prueba sin guardar nada.
 */
export const testSmtpConfiguration = async (
  config: SmtpSettings,
  recipient: string,
): Promise<SmtpTestResult> => {
  try {
    return await configService.testSmtpConfig(config, recipient);
  } catch (err) {
    Popups.notifyException(parseApiError(err));
    return { success: false, message: '' };
  }
};

/**
 * El backend vuelve a enviar el correo de prueba y responde 400 si no pasa,
 * de modo que la configuracion no se actualiza sin un test exitoso.
 */
export const saveSmtpConfiguration = async (
  config: SmtpSettings,
  recipient: string,
): Promise<boolean> => {
  try {
    await configService.saveSmtpConfig(config, recipient);
    return true;
  } catch (err) {
    Popups.notifyException(parseApiError(err));
    return false;
  }
};

export const getPrivacyUrl = async (): Promise<Settings | undefined> => {
  try {
    return configService.getSystemSettings();
  } catch (err) {
    Popups.notifyException(parseApiError(err));
  }
};

export const saveNewConfigurations =
  (
    systemConfig: SystemConfig,
    systemConfigInvoice: SystemConfigInvoice,
    settings: Settings,
    days: number,
    months: number,
    years: number,
    daysInvoice: number,
    monthsInvoice: number,
    yearsInvoice: number,
  ): ITA =>
  async (dispatch: IDA): Promise<void> => {
    try {
      dispatch(startFetch());
      await configService.saveNewConfigurations(
        systemConfig,
        systemConfigInvoice,
        settings,
        days,
        months,
        years,
        daysInvoice,
        monthsInvoice,
        yearsInvoice,
      );
      dispatch(endFetch());
      Popups.notifySuccess(
        'Las nuevas configuraciones se han guardado y seran usadas al momento',
      );
    } catch (err) {
      dispatch(endFetch());
      Popups.notifyException(parseApiError(err));
    }
  };
